// Screen touch controls (phones/tablets). No controller assumed.
// Left thumb: virtual stick (move on foot; when driving, point it where the car should go: see steerToward).
// Right thumb: aim stick; pushing past FIRE_AT (the ring on the stick) fires (fire-as-you-aim; the coilgun charges while held).
// Buttons are words, not glyphs, and only the ones that work right now are shown (on foot / driving / passenger / downed).
// Buttons emit the same Actions as the keyboard, so gameplay code has one path.
import type { Action } from './input';

const FIRE_AT = 0.55, DEAD = 0.12;
/** one-tap crew messages (CHAT opens them; typing mid-fight on a phone is too slow) */
export const QUICK = ['Get in!', 'Follow me', 'Hack that', 'Need help!', 'Cops!', 'Nice!'];
interface Stick { id: number | null; ox: number; oy: number; x: number; y: number; base: HTMLElement; knob: HTMLElement }

/** Which action buttons each situation shows (the rest are hidden, and the grid closes up). */
const SHOWN: Record<'foot' | 'driver' | 'passenger' | 'downed', string[]> = {
  foot: ['interact', 'reload', 'nextWeapon', 'throw', 'cycleThrow', 'hack', 'ability'],
  driver: ['interact', 'reload', 'hack', 'ability'], // reload is DRIFT (hold) at the wheel
  passenger: ['interact', 'reload', 'nextWeapon', 'hack', 'ability'],
  downed: [],
};

const wrapA = (a: number) => Math.atan2(Math.sin(a), Math.cos(a));
/** Stick-direction driving (client-side input shaping only: the server still gets an ordinary throttle/steer).
 *  The car turns toward where the stick points and drives there; pointing behind the car brakes, then backs up
 *  turning its nose round (a three-point turn). `reversing` is kept between calls so the choice doesn't flicker. */
export function steerToward(sx: number, sy: number, heading: number, reversing: boolean): { mx: number; my: number; reversing: boolean } {
  const mag = Math.min(1, Math.hypot(sx, sy));
  if (mag < DEAD) return { mx: 0, my: 0, reversing: false };
  const d = wrapA(Math.atan2(sy, sx) - heading); // + = the stick is clockwise of the nose
  const rev = Math.abs(d) > (reversing ? 1.4 : 1.95); // ~80° to go forward again, ~112° to start backing up
  // forward: steer by the angle left to turn; backing up: full lock the other way (reverse flips steering in sim.ts)
  const steer = rev ? -Math.sign(d) : Math.max(-1, Math.min(1, d * 2.2));
  return { mx: steer, my: rev ? mag : -mag, reversing: rev };
}

export class Touch {
  active = false;
  move = { x: 0, y: 0 };
  aim = { x: 0, y: 0, on: false, mag: 0 };
  pressed: Action[] = [];
  /** set when the player taps the world (not a control) — used for tap-to-select hack targets */
  tap: { x: number; y: number } | null = null;
  private hackMode = false;
  private reversing = false;
  root: HTMLElement;
  private left: Stick; private right: Stick;
  private btns = new Map<string, HTMLElement>();
  private hackRow: HTMLElement; private qRow: HTMLElement; private respawnBtn: HTMLElement;
  private said: string[] = [];
  private mode = '';

  constructor(private stage: HTMLElement, private toView: (cx: number, cy: number) => [number, number]) {
    this.root = document.createElement('div'); this.root.id = 'touch';
    this.root.innerHTML = `
      <div class="tzone left"></div><div class="tzone right"></div>
      <div class="tstick" id="tsl"><div class="tknob"></div></div>
      <div class="tstick" id="tsr"><div class="tring"></div><div class="tknob"></div></div>
      <div class="tbtns top">
        <button data-a="inventory" aria-label="Menu">MENU</button><button data-a="map" aria-label="Map">MAP</button><button data-a="quickchat" aria-label="Quick chat">CHAT</button><button data-a="ping" aria-label="Ping a spot for the crew">PING</button>
      </div>
      <div class="tbtns qchat">
        ${QUICK.map(t => `<button data-q="${t}">${t}</button>`).join('')}<button data-a="chat" aria-label="Type a message">TYPE…</button>
      </div>
      <div class="tbtns act">
        <button data-a="interact" class="big" aria-label="Use / enter vehicle">USE</button>
        <button data-a="reload" aria-label="Reload">RELOAD</button>
        <button data-a="nextWeapon" aria-label="Next weapon">SWAP<small>gun</small></button>
        <button data-a="throw" aria-label="Throw the selected throwable">THROW<small>✦</small></button>
        <button data-a="cycleThrow" aria-label="Switch throwable">NEXT<small>throwable</small></button>
        <button data-a="hack" aria-label="Quickhack">HACK</button>
        <button data-a="ability" aria-label="Cyberware ability">CYBER</button>
      </div>
      <div class="tbtns hackrow">
        <button data-a="hack1">SHORT<br>CIRCUIT</button><button data-a="hack2">SENSOR<br>JAM</button><button data-a="hack3">REMOTE<br>OVERRIDE</button>
      </div>
      <button class="tbtn respawn" data-a="respawn">RESPAWN AT CLINIC</button>
      <div id="rotate">Rotate your phone to landscape ↻</div>`;
    document.body.appendChild(this.root);
    const q = (s: string) => this.root.querySelector(s) as HTMLElement;
    this.left = { id: null, ox: 0, oy: 0, x: 0, y: 0, base: q('#tsl'), knob: q('#tsl .tknob') };
    this.right = { id: null, ox: 0, oy: 0, x: 0, y: 0, base: q('#tsr'), knob: q('#tsr .tknob') };
    this.hackRow = q('.hackrow'); this.qRow = q('.qchat'); this.respawnBtn = q('.respawn');
    this.qRow.querySelectorAll<HTMLElement>('button[data-q]').forEach(b => b.addEventListener('pointerdown', e => {
      e.preventDefault(); e.stopPropagation(); this.said.push(b.dataset.q!); this.quickChat(false); navigator.vibrate?.(8);
    }));
    this.root.querySelectorAll<HTMLElement>('button[data-a]').forEach(b => {
      this.btns.set(b.dataset.a!, b);
      b.addEventListener('pointerdown', e => { e.preventDefault(); e.stopPropagation(); this.pressed.push(b.dataset.a as Action); b.classList.add('down'); this.holding.add(b.dataset.a!); this.heldBy.set(e.pointerId, b); navigator.vibrate?.(8); });
      b.addEventListener('pointerleave', () => this.release(b));
    });
    this.trackPointers();
    const zl = q('.tzone.left'), zr = q('.tzone.right');
    this.bindStick(zl, this.left); this.bindStick(zr, this.right);
    // a quick tap on the right zone that never becomes a drag counts as a world tap (hack target select)
    addEventListener('touchstart', () => this.enable(), { passive: true });
    addEventListener('pointerdown', e => { if (e.pointerType === 'touch') this.enable(); }, { passive: true });
    // iPhone Safari ignores user-scalable=no: a pinch or double tap mid-fight zoomed the page
    for (const t of ['gesturestart', 'gesturechange', 'dblclick']) document.addEventListener(t, e => { if (this.active) e.preventDefault(); }, { passive: false });
    // phones/tablets (coarse primary pointer, no mouse): start in touch mode right away
    if (matchMedia('(pointer: coarse)').matches && !matchMedia('(any-pointer: fine)').matches) this.enable();
  }
  enable() {
    if (this.active) return;
    this.active = true; document.body.classList.add('touch');
    // best effort: fullscreen + landscape lock (Android Chrome allows both after a user gesture; iPhone has neither)
    const el = document.documentElement as any;
    try { el.requestFullscreen?.({ navigationUI: 'hide' })?.catch?.(() => {}); } catch { /* ignore */ }
    try { (screen.orientation as any)?.lock?.('landscape')?.catch?.(() => {}); } catch { /* ignore */ }
  }
  private bindStick(zone: HTMLElement, s: Stick) {
    const R = () => Math.max(40, Math.min(innerWidth, innerHeight) * 0.13);
    let downAt = 0, moved = false;
    zone.addEventListener('pointerdown', e => {
      // a slot is only "busy" if its finger is still down; a lost pointerup must not lock the stick forever
      if (s.id !== null && this.down.has(s.id)) return;
      e.preventDefault(); this.enable();
      zone.setPointerCapture(e.pointerId);
      s.id = e.pointerId; s.ox = e.clientX; s.oy = e.clientY; s.x = 0; s.y = 0; downAt = e.timeStamp; moved = false;
      s.base.style.display = 'block'; s.base.style.left = `${e.clientX}px`; s.base.style.top = `${e.clientY}px`;
      s.knob.style.transform = 'translate(-50%,-50%)';
    });
    zone.addEventListener('pointermove', e => {
      if (e.pointerId !== s.id) return;
      let dx = (e.clientX - s.ox) / R(), dy = (e.clientY - s.oy) / R();
      const l = Math.hypot(dx, dy); if (l > 1) { dx /= l; dy /= l; }
      if (l > 0.2) moved = true;
      s.x = dx; s.y = dy;
      s.knob.style.transform = `translate(calc(-50% + ${dx * R()}px), calc(-50% + ${dy * R()}px))`;
      this.sync();
    });
    const end = (e: PointerEvent) => {
      if (e.pointerId !== s.id) return;
      // use the events' own timestamps: on a busy device the handlers may run late, but the touch itself was quick
      // in hack mode the right thumb doesn't aim or shoot, so any press-and-release without dragging is a tap
      if (s === this.right && !moved && (this.hackMode || e.timeStamp - downAt < 400)) this.tap = { x: e.clientX, y: e.clientY };
      s.id = null; s.x = 0; s.y = 0; s.base.style.display = 'none'; this.sync();
    };
    zone.addEventListener('pointerup', end); zone.addEventListener('pointercancel', end);
    zone.addEventListener('lostpointercapture', e => { if (e.pointerId === s.id) { s.id = null; s.x = 0; s.y = 0; s.base.style.display = 'none'; this.sync(); } });
  }
  /** pointers currently touching the screen (tracked globally, so stale stick slots can be detected) */
  private down = new Set<number>();
  /** which button each finger holds: a release anywhere frees it, since a button's own pointerup can go missing
   *  (the press relabels it, a phone gesture steals the touch) and a stuck DRIFT is a stuck handbrake */
  private heldBy = new Map<number, HTMLElement>();
  private release(b: HTMLElement) { b.classList.remove('down'); this.holding.delete(b.dataset.a!); for (const [id, x] of this.heldBy) if (x === b) this.heldBy.delete(id); }
  private trackPointers() {
    addEventListener('pointerdown', e => this.down.add(e.pointerId), { capture: true, passive: true });
    for (const t of ['pointerup', 'pointercancel'] as const) addEventListener(t, e => {
      this.down.delete(e.pointerId); const b = this.heldBy.get(e.pointerId); if (b) this.release(b);
    }, { capture: true, passive: true });
  }
  debug() { return { left: this.left.id, right: this.right.id, down: [...this.down], tap: this.tap }; }
  private sync() {
    const dz = (v: number, l: number) => (l < DEAD ? 0 : v);
    const ll = Math.hypot(this.left.x, this.left.y), rl = Math.hypot(this.right.x, this.right.y);
    this.move = { x: dz(this.left.x, ll), y: dz(this.left.y, ll) };
    this.aim = { x: this.right.x, y: this.right.y, on: rl > DEAD, mag: rl };
    this.right.base.classList.toggle('fire', this.fire && !this.hackMode); // the knob turns red past the ring: shooting
  }
  get fire() { return this.aim.on && this.aim.mag >= FIRE_AT; }
  /** the left stick as a drive command for a car whose nose points at `heading` */
  drive(heading: number) { const r = steerToward(this.move.x, this.move.y, heading, this.reversing); this.reversing = r.reversing; return { mx: r.mx, my: r.my }; }
  /** buttons whose finger is still down (the RELOAD button doubles as the handbrake while driving) */
  private holding = new Set<string>();
  held(a: Action) { return this.holding.has(a); }
  take(): Action[] { const p = this.pressed; this.pressed = []; return p; }
  /** quick-chat messages tapped since the last call */
  takeSaid(): string[] { const s = this.said; this.said = []; return s; }
  /** show/hide the quick-chat row (toggles when no argument) */
  quickChat(open = !this.qRow.classList.contains('open')) {
    this.qRow.classList.toggle('open', open); this.btns.get('quickchat')?.classList.toggle('on', open);
  }
  takeTap(): [number, number] | null { const t = this.tap; this.tap = null; return t ? this.toView(t.x, t.y) : null; }
  /** Context: relabel / show buttons for the current situation. Runs every frame, so it only touches the DOM on change. */
  update(o: { driving: boolean; inVehicle: boolean; downed: boolean; hackMode: boolean; canUse: boolean; menuOpen: boolean; throwIcon?: string }) {
    this.hackMode = o.hackMode;
    if (!this.active) return;
    const mode = o.downed ? 'downed' : o.driving ? 'driver' : o.inVehicle ? 'passenger' : 'foot';
    if (mode !== this.mode) {
      this.mode = mode;
      const shown = SHOWN[mode as keyof typeof SHOWN];
      for (const a of ['interact', 'reload', 'nextWeapon', 'throw', 'cycleThrow', 'hack', 'ability']) this.btns.get(a)!.hidden = !shown.includes(a);
      this.btns.get('interact')!.textContent = o.inVehicle ? 'EXIT' : 'USE';
      this.btns.get('reload')!.innerHTML = o.driving ? 'DRIFT<small>hold</small>' : 'RELOAD';
      if (!o.driving) this.reversing = false;
    }
    const icon = this.btns.get('throw')!.querySelector('small')!;
    if (o.throwIcon && icon.textContent !== o.throwIcon) icon.textContent = o.throwIcon; // the selected throwable's glyph
    this.btns.get('interact')!.classList.toggle('hint', o.canUse && !o.inVehicle);
    this.btns.get('hack')!.classList.toggle('on', o.hackMode);
    const hackRow = o.hackMode && !o.driving ? 'flex' : 'none', respawn = o.downed ? 'block' : 'none';
    if (this.hackRow.style.display !== hackRow) this.hackRow.style.display = hackRow;
    if (this.respawnBtn.style.display !== respawn) this.respawnBtn.style.display = respawn;
    this.root.classList.toggle('menu', o.menuOpen);
  }
}
